// Phase 1A Phase 7B — useCompoundingDecision hook.
//
// Drives the post-win compounding decision surface inside The Vault:
//
//   1. computeSuggestion(cycleId) — calls compute_kelly_suggestion (RO,
//      Migration 0029) so the panel can render a deterministic suggested
//      stake + protected amount BEFORE the user picks an option. The RPC
//      is non-mutating; calling it on render is safe.
//   2. fetchExistingDecision(cycleId) — reads
//      rollover_compounding_decisions for this cycle. RLS scopes to the
//      caller, so no user_id eq filter is needed.
//   3. confirmDecision(...) — calls confirm_compounding_decision
//      (Migration 0030, SECURITY DEFINER, write-once). Idempotent on the
//      server; double-tap returns already_confirmed=true.
//
// What this hook does NOT do:
//   - No randomness. No Math.random / crypto.randomUUID() in business
//     paths. The RPCs do all the math server-side.
//   - No write-around. Direct INSERT/UPDATE/DELETE on the new tables is
//     blocked by RLS (verify-compounding-decisions Path 6). Only the
//     RPC writes.
//   - No real-money paths. Phase 1A is simulated=true throughout — the
//     RPC's CHECK constraints and the verify suite enforce this.

import { useCallback, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { useComposedQuery } from "../data/useTableQuery.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

type DecisionRow = Database["public"]["Tables"]["rollover_compounding_decisions"]["Row"];

export interface KellySuggestion {
  win_amount: number;
  suggested_stake: number;
  protected_amount: number;
  raw_kelly_fraction: number;
  capped_fraction: number;
  reasoning: string;
}

export type CompoundingOption = "suggested" | "full" | "original" | "custom";

export interface ConfirmReturn {
  decision_id: string;
  selected_option: CompoundingOption;
  confirmed_stake: number;
  confirmed_protected_amount: number;
  cumulative_protected_amount: number;
  next_chain_value: number;
  warnings: string[];
  already_confirmed: boolean;
  agent_reasoning: string;
}

export interface CompoundingDecisionState {
  suggestion: KellySuggestion | null;
  existingDecision: DecisionRow | null;
  loading: boolean;
  busy: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  confirmDecision: (
    cycleId: string,
    option: CompoundingOption,
    customStake?: number,
  ) => Promise<{ data: ConfirmReturn | null; error: string | null }>;
}

function asN(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") return Number(v);
  return Number.NaN;
}

// Composite the read returns; carries the hook's exact non-fatal error rules
// (dec error → both null; sug error → suggestion null but existingDecision kept).
interface CompoundingReadResult {
  suggestion: KellySuggestion | null;
  existingDecision: DecisionRow | null;
  error: string | null;
}

export function useCompoundingDecision(cycleId: string | null): CompoundingDecisionState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [busy, setBusy] = useState(false);
  // confirmDecision surfaces its own error; layered over the read error.
  const [mutationError, setMutationError] = useState<string | null>(null);

  const enabled = Boolean(user) && cycleId !== null;

  const q = useComposedQuery<CompoundingReadResult>(
    ["compounding-decision", user?.id ?? null, cycleId],
    async (client): Promise<CompoundingReadResult> => {
      if (cycleId === null) return { suggestion: null, existingDecision: null, error: null };
      // Existing decision (RLS-scoped — no user_id filter needed).
      const dec = await client
        .from("rollover_compounding_decisions")
        .select("*")
        .eq("cycle_id", cycleId)
        .maybeSingle();
      if (dec.error) {
        return { suggestion: null, existingDecision: null, error: dec.error.message };
      }
      const existingDecision = (dec.data as DecisionRow | null) ?? null;

      // Kelly suggestion (RO RPC).
      const sug = await client.rpc("compute_kelly_suggestion", { p_cycle_id: cycleId });
      if (sug.error) {
        // Non-fatal: a busted/confirmed cycle will return an RPC error.
        // The Vault page suppresses the panel entirely in those cases.
        return { suggestion: null, existingDecision, error: sug.error.message };
      }
      const data = sug.data as Record<string, unknown> | null;
      const suggestion: KellySuggestion | null = data
        ? {
            win_amount: asN(data.win_amount),
            suggested_stake: asN(data.suggested_stake),
            protected_amount: asN(data.protected_amount),
            raw_kelly_fraction: asN(data.raw_kelly_fraction),
            capped_fraction: asN(data.capped_fraction),
            reasoning: typeof data.reasoning === "string" ? data.reasoning : "",
          }
        : null;
      return { suggestion, existingDecision, error: null };
    },
    { enabled },
  );

  const suggestion = enabled ? (q.data?.suggestion ?? null) : null;
  const existingDecision = enabled ? (q.data?.existingDecision ?? null) : null;
  const error = !enabled
    ? null
    : (mutationError ?? q.data?.error ?? (q.error ? q.error.message : null));
  const loading = enabled ? q.isLoading : false;

  const refetch = useCallback(async (): Promise<void> => {
    setMutationError(null);
    await q.refetch();
  }, [q]);

  const confirmDecision = useCallback(
    async (
      cycleIdInput: string,
      option: CompoundingOption,
      customStake?: number,
    ): Promise<{ data: ConfirmReturn | null; error: string | null }> => {
      if (busy) return { data: null, error: null };
      setBusy(true);
      setMutationError(null);
      const args: { p_cycle_id: string; p_selected_option: string; p_custom_stake?: number } = {
        p_cycle_id: cycleIdInput,
        p_selected_option: option,
      };
      if (option === "custom" && typeof customStake === "number") {
        args.p_custom_stake = customStake;
      }
      const r = await supabase.rpc("confirm_compounding_decision", args);
      if (r.error || !r.data) {
        const msg = r.error?.message ?? "confirm returned no data";
        setMutationError(msg);
        setBusy(false);
        return { data: null, error: msg };
      }
      const d = r.data as Record<string, unknown>;
      const out: ConfirmReturn = {
        decision_id: String(d.decision_id ?? ""),
        selected_option: String(d.selected_option ?? "suggested") as CompoundingOption,
        confirmed_stake: asN(d.confirmed_stake),
        confirmed_protected_amount: asN(d.confirmed_protected_amount),
        cumulative_protected_amount: asN(d.cumulative_protected_amount),
        next_chain_value: asN(d.next_chain_value),
        warnings: Array.isArray(d.warnings) ? (d.warnings as string[]) : [],
        already_confirmed: d.already_confirmed === true,
        agent_reasoning: typeof d.agent_reasoning === "string" ? d.agent_reasoning : "",
      };
      await refetch();
      setBusy(false);
      return { data: out, error: null };
    },
    [busy, supabase, refetch],
  );

  return useMemo<CompoundingDecisionState>(
    () => ({
      suggestion,
      existingDecision,
      loading,
      busy,
      error,
      refetch,
      confirmDecision,
    }),
    [suggestion, existingDecision, loading, busy, error, refetch, confirmDecision],
  );
}
